import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listResolutions, listCompanies } from "@/lib/api";
import { FileText, Sparkles } from "lucide-react";

export default function ResolutionsList() {
  const [rs, setRs] = useState([]);
  const [cs, setCs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listResolutions(), listCompanies()])
      .then(([r, c]) => { setRs(r.data || []); setCs(c.data || []); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter ? rs.filter((r) => r.company_id === filter) : rs;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="resolutions-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Archive</div>
          <h1 className="font-heading text-4xl font-light tracking-tight">Resolutions</h1>
        </div>
        <Link to="/generate" className="swiss-btn-primary" data-testid="new-resolution-cta">
          <Sparkles size={16} /> New Resolution
        </Link>
      </div>

      <div className="mb-6">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          data-testid="company-filter"
        >
          <option value="">All companies</option>
          {cs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-12 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-16 text-center" data-testid="resolutions-empty">
          <FileText size={32} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-4" />
          <h2 className="font-heading text-xl font-medium mb-2">No resolutions yet</h2>
          <p className="text-zinc-500 mb-6 text-sm">Draft your first AI-powered board resolution.</p>
          <Link to="/generate" className="swiss-btn-primary" data-testid="generate-first">
            <Sparkles size={16} /> Generate Resolution
          </Link>
        </div>
      ) : (
        <div className="border border-zinc-200" data-testid="resolutions-list">
          {filtered.map((r, i) => (
            <Link
              key={r.id}
              to={`/resolutions/${r.id}`}
              className={`grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 p-5 ${i > 0 ? "border-t border-zinc-100" : ""} hover:bg-zinc-50`}
              data-testid={`resolution-row-${r.id}`}
            >
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono-plex text-xs text-zinc-500">{r.resolution_number}</span>
                  {r.is_special_resolution && (
                    <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[#002FA7] text-white">Special</span>
                  )}
                  {r.is_locked && (
                    <span
                      className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-amber-50 border border-amber-300 text-amber-800"
                      data-testid={`row-locked-${r.id}`}
                    >
                      Signed &amp; Final
                    </span>
                  )}
                </div>
                <h3 className="font-medium">{r.resolution_title}</h3>
                <div className="text-xs text-zinc-500 mt-1">{r.company_name}</div>
              </div>
              <div className="text-sm text-zinc-600 md:text-right">{r.meeting_date}</div>
              <div className="label-eyebrow self-center">View →</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
